import {
  Actor,
  deterministicUuid,
  StepDefinitionId,
  StepInstanceId,
  WILLOW_INTERNAL_ACTOR,
  WorkflowInstanceId,
} from '@willow/types-iso';

import { StepDefinition, StepType } from '../definitions/steps/StepDefinition';
import { SerializedStepInstance } from './SerializedStepInstance';
import { SerializedWorkflowInstance } from './SerializedWorkflowInstance';

export interface ReconciledStep {
  serialized: SerializedStepInstance<StepType>;
  definition: StepDefinition;
}

export interface ReconciledSteps {
  steps: ReconciledStep[];
  orphans: SerializedStepInstance<StepType>[];
  backfilled: boolean;
}

export const reconcileSteps = (
  instance: SerializedWorkflowInstance,
  stepDefinitions: StepDefinition[],
  actor: Actor = WILLOW_INTERNAL_ACTOR,
): ReconciledSteps => {
  const stored = instance.serializedSteps;
  // Instances saved before steps carried a stepDefinitionId were written in definition order. Once any
  // step has an id, position is meaningless: an orphan appended by an earlier save must not be paired
  // with whatever definition step now sits at its index.
  const byPosition = stored.length > 0 && stored.every((step) => step.stepDefinitionId == null);
  if (byPosition) {
    // Pairing a misaligned legacy array by position would stamp the wrong ids on the next save, so refuse.
    const aligned =
      stored.length === stepDefinitions.length && stored.every((step, i) => step.type === stepDefinitions[i].type);
    if (!aligned) {
      throw new Error(
        `Workflow instance ${instance.id} has ${stored.length} steps without a stepDefinitionId; definition ${instance.definitionId} has ${stepDefinitions.length} steps, so they cannot be paired by position`,
      );
    }
  }
  const byDefinitionId = new Map(stored.map((step) => [step.stepDefinitionId, step]));

  const matched = new Set<SerializedStepInstance<StepType>>();
  let backfilled = false;
  const steps = stepDefinitions.map((definition, i): ReconciledStep => {
    const candidate = byPosition ? stored[i] : byDefinitionId.get(definition.id);
    if (candidate == null) {
      backfilled = true;
      return { serialized: compileSerializedStep(instance, definition, actor), definition };
    }
    if (candidate.type !== definition.type) {
      throw new Error(
        `Workflow instance ${instance.id} stores step ${definition.id} as ${candidate.type} but definition ${instance.definitionId} declares it as ${definition.type}; a step that changes type needs a new id`,
      );
    }
    matched.add(candidate);
    return { serialized: candidate, definition };
  });

  return { steps, orphans: stored.filter((step) => !matched.has(step)), backfilled };
};

// A step compiled during a read reaches the UI before anything persists it, and the UI mutates it by
// id, so every read must mint the same id. A random id would dangle as soon as the page re-fetched.
export const stableStepInstanceId = (
  workflowInstanceId: WorkflowInstanceId,
  stepDefinitionId: StepDefinitionId,
): StepInstanceId => deterministicUuid(`workflow-step:${workflowInstanceId}:${stepDefinitionId}`) as StepInstanceId;

export const compileSerializedStep = (
  instance: Pick<SerializedWorkflowInstance, 'id' | 'launchTime'>,
  stepDefinition: StepDefinition,
  actor: Actor,
): SerializedStepInstance<StepType> => ({
  id: stableStepInstanceId(instance.id, stepDefinition.id),
  stepDefinitionId: stepDefinition.id,
  type: stepDefinition.type,
  displayInfo: [],
  statusHistory: [
    {
      status: 'inactive',
      timestamp: instance.launchTime,
      actor,
      manual: false,
    },
  ],
  formSubmissions: [],
  retryCondition: undefined,
  dueAt: { type: 'dueUnset' },
  isVisible: false,
  canManuallyActivate: false,
  canManuallySkip: false,
  canManuallyComplete: false,
  canManuallyFail: false,
  canEditAfterCompletion: false,
  willActivateOn: undefined,
  actionsToTake: [],

  assignmentHistory: [],

  notes: [],
});
